import { useEffect, useState } from "react";
import PunctualCourseRepository from "../../Repositories/PunctualCourseRepository";
import { groupAmount, groupRows } from "./courseRows";

export interface PendingSummary { count: number; students: number; halfHours: number }
/** `amount` : prix total TTC chez Verified Trainer, rémunération ailleurs (comme la colonne du tableau). */
export interface MonthSummary { count: number; students: number; amount: number }

export interface CoursesSummary {
    pending: PendingSummary | null;
    month: MonthSummary | null;
    /** Nombre de brouillons (lignes), tous élèves confondus. */
    drafts: number | null;
    /** Nombre de cours passés, toutes périodes. */
    past: number | null;
}

const EMPTY: CoursesSummary = { pending: null, month: null, drafts: null, past: null };
/** Au-delà de cent lignes, les listes À venir, Ce mois et Brouillons sont déjà hors norme : on résume ce qui est chargé. */
const SUMMARY_LIMIT = 100;

/**
 * Compteurs des onglets et chiffres des sous-titres de « Mes cours » repensé,
 * depuis les mêmes appels que les listes (une page large par liste, une page
 * d'un élément pour le total des passés). Rafraîchis quand `refreshKey` change.
 */
export default function useCoursesSummary(teachrId: number | null | undefined, refreshKey: string): { summary: CoursesSummary; loading: boolean } {
    const [summary, setSummary] = useState<CoursesSummary>(EMPTY);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!teachrId) return;
        let cancelled = false;
        setLoading(true);
        Promise.allSettled([
            PunctualCourseRepository.getPendingPunctualCourseByTeachr(teachrId, 0, SUMMARY_LIMIT),
            PunctualCourseRepository.getPunctualCourseByTeachr(teachrId, 0, SUMMARY_LIMIT),
            PunctualCourseRepository.getCourseNextMonth(teachrId, 0, SUMMARY_LIMIT),
            PunctualCourseRepository.searchPunctualCoursePastByTeachrNew(0, teachrId, "", "", "", "", 1),
        ]).then(([pending, month, drafts, past]) => {
            if (cancelled) return;
            const next: CoursesSummary = { ...EMPTY };
            if (pending.status === "fulfilled" && pending.value) {
                const rows: any[] = pending.value.courses ?? [];
                next.pending = {
                    count: pending.value.totalItems ?? rows.length,
                    students: new Set(rows.map((c) => c?.forChild_id ?? c?.clientId ?? c?.id)).size,
                    halfHours: rows.reduce((n, c) => n + (Number(c?.duration ?? 0) || 0), 0),
                };
            }
            if (month.status === "fulfilled" && month.value) {
                const groups: any[] = month.value.courses ?? [];
                next.month = {
                    count: groups.reduce((n, g) => n + groupRows(g).length, 0),
                    students: month.value.totalItems ?? groups.length,
                    amount: groups.reduce((n, g) => n + groupAmount(g), 0),
                };
            }
            if (drafts.status === "fulfilled" && drafts.value) {
                const groups: any[] = drafts.value.courses ?? [];
                next.drafts = groups.reduce((n, g) => n + groupRows(g).length, 0);
            }
            if (past.status === "fulfilled" && past.value) {
                next.past = past.value.totalItems ?? null;
            }
            setSummary(next);
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [teachrId, refreshKey]);

    return { summary, loading };
}
